import { useEffect, lazy, Suspense } from "react";
import { importWithReload } from "@/lib/chunk-reload";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { I18nextProvider } from "react-i18next";

import i18n, { getFontScale } from "@/lib/i18n";
import { queryClient } from "@/lib/queryClient";
import { useSession } from "@/lib/auth";
import { Layout } from "@/components/Layout";
import { GuestFilePage } from "@/pages/GuestFilePage";
import { InvitationPage } from "@/pages/InvitationPage";
import { LoginPage } from "@/pages/LoginPage";
import { RoomsPage } from "@/pages/RoomsPage";
import { AgentsPage } from "@/pages/AgentsPage";
import { SearchPage } from "@/pages/SearchPage";
import { HomePage } from "@/pages/HomePage";
import { RoomPage } from "@/pages/RoomPage";
import { NotificationTenantGuard } from '@/components/NotificationTenantGuard';
import { TasksPage } from "@/pages/TasksPage";
import { FilesPage } from "@/pages/FilesPage";
import { AiPersonalWorkflowAssistantPage } from "@/pages/AiPersonalWorkflowAssistantPage";
const CallPage = lazy(importWithReload(() => import("@/pages/CallPage").then(module => ({ default: module.CallPage }))));
import { SettingsPage } from "@/pages/SettingsPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { LegalPrivacyPage } from "@/pages/LegalPrivacyPage";
import { LegalTermsPage } from "@/pages/LegalTermsPage";
import { LegalAccountDeletionPage } from "@/pages/LegalAccountDeletionPage";

import "@/index.css";

export default function App(): JSX.Element {
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("data-font-scale", getFontScale());
    }
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route path="/guest/:token" element={<GuestFilePage />} />
            <Route path="/auth/invitation" element={<InvitationPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/oauth/callback" element={<LoginPage />} />
            <Route path="/legal/privacy" element={<LegalPrivacyPage />} />
            <Route path="/legal/terms" element={<LegalTermsPage />} />
            <Route path="/legal/account-deletion" element={<LegalAccountDeletionPage />} />
            <Route path="/discover/ai-workflow" element={<AiPersonalWorkflowAssistantPage />} />
            <Route element={<RequireAuth />}>
              <Route element={<Layout />}>
                <Route index element={<HomePage />} />
                <Route path="rooms" element={<RoomsPage />} />
                <Route path="dm" element={<RoomsPage />} />
                <Route path="rooms/:id" element={<NotificationTenantGuard><RoomPage /></NotificationTenantGuard>} />
                <Route path="agents" element={<AgentsPage />} />
                <Route path="search" element={<SearchPage />} />
                <Route path="tasks" element={<TasksPage />} />
                <Route path="files" element={<FilesPage />} />
                <Route path="calls/:id?" element={<Suspense fallback={<p role="status" className="p-6">通話画面を読み込んでいます…</p>}><CallPage /></Suspense>} />
                <Route path="settings/*" element={<NotificationTenantGuard><SettingsPage /></NotificationTenantGuard>} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Route>
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </I18nextProvider>
  );
}

import { Outlet } from "react-router-dom";
function RequireAuth(): JSX.Element {
  const session = useSession();
  const location = useLocation();
  if (!session) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <Outlet />;
}
